/**
 * Guide Module Content
 *
 * Context-specific guide commentary for each module.
 * Each guide has unique intros, section tips, and completion messages.
 *
 * Australian English throughout.
 */

/**
 * Module intro messages - shown when starting a module
 */
export const MODULE_INTROS = {
  // Module 1: Your Social Brain — the drop after a story you weren't in,
  // the security desk, alarm-before-brakes, and the kit for the ten minutes after.
  1: {
    sage: "This one is about the ten minutes after something small — a story you weren't in, a reply that took too long. Once you can see what your brain does in that gap, the pattern stops running you.",
    jaz: "Being left out can hurt like an injury, and there's real research on why. This module walks you through it, then gives you the kit for the ten minutes after. Start here.",
    mika: "Two cartoon hands stopped passing someone a ball, and a brain scanner picked up how much it stung. That study, and what later research made of it, is where this module starts.",
    riley: "A story you weren't in, and the drop that follows. Everyone gets that drop. This module is about what it is, why it's louder at your age, and how to sit through it without doing damage.",
    alex: "I've sent the 11pm paragraph. More than once. This module is the part I wish someone had shown me first — why the drop happens, and what to do in the gap before you send anything.",
    sam: "If being left out lands harder for you than it seems to for other people, there's a reason, and it isn't weakness. Take this one at your own pace — it's the gentlest place to start.",
  },

  // Module 2: Group Dynamics
  2: {
    sage: "Groups have invisible structures — hierarchies, alliances, patterns. Once you can see them, you can navigate them much more effectively. Let's decode them together.",
    jaz: "Once you understand group dynamics, you stop taking things personally. That shift is HUGE. Ready to see what's really going on?",
    mika: "People are quick to notice who's in, who's out and who's in charge in a group. This module looks at what the research actually says about how groups work.",
    riley: "Every group is like an ecosystem — different roles, different energies, all interconnected. Understanding your place in it helps everything flow better.",
    alex: "I spent so long not understanding why I felt weird in groups. Turns out there were dynamics I just couldn't see. This module makes the invisible visible.",
    sam: "Group situations can feel overwhelming sometimes, and that's okay. This module will help you feel more grounded and secure when you're with others.",
  },

  // Module 3: Conflict Resolution
  3: {
    sage: "Conflict often reveals deeper patterns in relationships. Instead of avoiding it, we can learn to understand what it's really about. There's wisdom in disagreement.",
    jaz: "Okay, conflict is uncomfortable — but avoiding it is worse. This module teaches you how to handle it without losing yourself or the friendship. Let's do this.",
    mika: "In an argument your body can flood with stress, and it gets harder to think clearly, which is why you say things you regret. This module helps you notice it and work with it.",
    riley: "Storms are part of nature, and conflict is part of relationships. The question isn't whether storms happen, but how we weather them together.",
    alex: "I used to be terrible at conflict. Either I'd avoid it completely or I'd explode. This module has the stuff I wish I'd known years ago.",
    sam: "Conflict can feel really scary, and I want to acknowledge that first. We'll take this gently, with lots of space to process what comes up.",
  },

  // Module 4: Digital Drama
  4: {
    sage: "Digital communication strips away so much context — tone, facial expressions, timing. Understanding this helps explain why drama escalates online so easily.",
    jaz: "Digital drama is REAL and it's exhausting. Let's build you a toolkit for handling online conflict without it ruining your day — or your friendships.",
    mika: "Texts lose tone of voice, faces and timing, which is a big part of why they get misread. This module explains what the research says about it.",
    riley: "The digital world moves fast, but you don't have to match its pace. There's power in slowing down, stepping back, and responding with intention.",
    alex: "I've been in so many text fights that could've been avoided with one phone call. This module helps you recognise when to take things offline.",
    sam: "Online spaces can feel intense and overwhelming. It's okay to protect your peace. This module is about boundaries, not just drama management.",
  },

  // Module 5: Friendship Endings
  5: {
    sage: "Endings are a form of transformation, not just loss. There's often wisdom in understanding why a friendship no longer serves both people.",
    jaz: "This one's tough, I'm not gonna lie. Friendship endings hurt. But understanding them — and yourself — helps you heal and grow. I've got you.",
    mika: "Being rejected genuinely hurts, and researchers have spent years studying why. No wonder friendship endings hurt so much.",
    riley: "Like leaves falling in autumn, some friendships have seasons. Endings aren't failures — they're part of the cycle of growth and renewal.",
    alex: "I've lost friendships I thought would last forever. It's brutal. This module won't make it painless, but it'll help you process and move forward.",
    sam: "Losing a friendship is a real grief, and your feelings about it are completely valid. We'll move through this gently, at whatever pace you need.",
  },

  // Module 6: Finding Your People
  6: {
    sage: "Finding genuine connection requires understanding what you actually value in friendship. Let's explore who your people really are — and where to find them.",
    jaz: "Your people are out there. You just need to know how to find them — and how to be findable yourself. Let's work on both.",
    mika: "People tend to become friends with the people they see again and again. This module shows you the research, and how to use it to widen your world.",
    riley: "Like attracts like, but you have to be visible to be found. This module is about both knowing yourself and putting yourself in the right places.",
    alex: "Making new friends as you get older feels weird — I get it. But it's also possible and actually pretty great. This module shows you how.",
    sam: "It's brave to want to expand your social world. This module is about finding people who appreciate you exactly as you are.",
  },

  // Module 7: Social Confidence
  7: {
    sage: "Confidence isn't about never feeling nervous — it's about understanding those feelings and acting meaningfully despite them. Let's examine this together.",
    jaz: "Confidence isn't pretending you're not nervous. It's showing up anyway and proving to yourself that you can. This module builds that muscle.",
    mika: "Nerves usually get easier with practice. This module has the research behind that, and small steps to try.",
    riley: "Confidence grows like a tree — slowly, steadily, with deep roots. Each small brave action adds another ring of strength.",
    alex: "I still get nervous in new social situations. The difference now is I know how to work with it instead of against it. That's what this module teaches.",
    sam: "Social anxiety is really common, and there's nothing wrong with you for experiencing it. This module is about compassionate growth, not forcing yourself.",
  },

  // Module 8: Friendship Mastery
  8: {
    sage: "Mastery isn't a destination — it's a practice of continuous refinement. This module integrates everything you've learned into a coherent approach to friendship.",
    jaz: "You've made it to the advanced stuff! This is where everything comes together. Time to put it all to work for real.",
    mika: "This module pulls the earlier ones together, so you can use them in real situations rather than just know them.",
    riley: "You've planted many seeds throughout this journey. Now we tend the whole garden — seeing how everything connects and grows together.",
    alex: "Getting to this module means you've already done the work. Now it's about making it stick and handling the complex stuff life throws at you.",
    sam: "I'm so proud of how far you've come. This module is about integrating everything with self-compassion — mastery without perfectionism.",
  },
};

/**
 * Section transition tips - shown when moving between sections
 * These are more dynamic and guide-personality specific
 */
export const SECTION_TIPS = {
  sage: [
    "I noticed you're moving through steadily. Take a moment to reflect on what patterns you're seeing.",
    "There's often more beneath the surface. What's resonating with you so far?",
    "Each section builds on the last. Can you see the connections emerging?",
  ],
  jaz: [
    "Nice progress! You're building real skills here. Keep that momentum going.",
    "This next bit is really practical — pay attention to the examples.",
    "You're doing great. Seriously.",
  ],
  mika: [
    "Ooh, the next section has some really interesting research behind it!",
    "Reading is the start. Trying it out is what makes it stick.",
    "The science in this next part is fascinating — your brain is going to love it.",
  ],
  riley: [
    "Take a breath before continuing. Let what you've learned settle.",
    "Like walking a path, sometimes we need to pause and notice where we are.",
    "The next section deepens what you've already begun. No rush.",
  ],
  alex: [
    "This next part hit me hard when I first learned it. Stay open to it.",
    "Some of this might feel uncomfortable. That's normal.",
    "I remember learning this and thinking 'why didn't anyone tell me this earlier?'",
  ],
  sam: [
    "You're doing beautifully. Take all the time you need with each section.",
    "Check in with yourself — how are you feeling? It's okay to pause.",
    "The next section might bring up some feelings. I'm here with you.",
  ],
};

/**
 * Exercise encouragement - shown during exercises
 */
export const EXERCISE_ENCOURAGEMENT = {
  sage: "Exercises like this reveal patterns you might not see otherwise. Approach it with curiosity.",
  jaz: "This is where the real learning happens. Don't overthink it — just engage honestly.",
  mika: "Trying something out tends to stick better than just reading about it. Give it a go.",
  riley: "Like practising any skill, the value is in the doing. Be patient with yourself.",
  alex: "I know exercises can feel awkward, but they're how you actually internalise this stuff. Trust the process.",
  sam: "There's no wrong way to do this exercise. Whatever comes up for you is valid.",
};

/**
 * Module completion messages - shown when finishing a module
 */
export const MODULE_COMPLETION = {
  sage: "You've completed the module. I noticed real engagement in how you worked through it. The patterns you've learned will continue to reveal themselves.",
  jaz: "YES! You finished it! That's not nothing. Give yourself credit for showing up and doing the work.",
  mika: "Module done. It sticks if you use it, so pick one thing to try this week.",
  riley: "Another chapter in your journey complete. Like rings in a tree, this growth is now part of you. Take a moment to appreciate how far you've come.",
  alex: "You did it! I know some of that probably hit close to home. That's actually a good sign — it means you're engaging honestly with the material.",
  sam: "I'm really proud of you for completing this module. That took real commitment. Please take a moment to acknowledge yourself for this.",
};

/**
 * Get a random section tip for a guide
 */
export const getRandomSectionTip = (guideId) => {
  const tips = SECTION_TIPS[guideId] || SECTION_TIPS.sage;
  return tips[Math.floor(Math.random() * tips.length)];
};

/**
 * Get module intro for specific module and guide
 */
export const getModuleIntro = (moduleId, guideId) => {
  const moduleIntros = MODULE_INTROS[moduleId];
  if (!moduleIntros) return null;
  return moduleIntros[guideId] || moduleIntros.sage;
};

/**
 * Get exercise encouragement for a guide
 */
export const getExerciseEncouragement = (guideId) => {
  return EXERCISE_ENCOURAGEMENT[guideId] || EXERCISE_ENCOURAGEMENT.sage;
};

/**
 * Get module completion message for a guide
 */
export const getModuleCompletion = (guideId) => {
  return MODULE_COMPLETION[guideId] || MODULE_COMPLETION.sage;
};

export default {
  MODULE_INTROS,
  SECTION_TIPS,
  EXERCISE_ENCOURAGEMENT,
  MODULE_COMPLETION,
  getRandomSectionTip,
  getModuleIntro,
  getExerciseEncouragement,
  getModuleCompletion,
};
